import React, { useContext, useState } from 'react';
import { useLocation } from '@reach/router';
import { sendPasswordResetEmail, AuthErrorCodes } from 'firebase/auth';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { H1, H3 } from '@components/dashboard/Typography';
import BackArrowSmallIcon from '@images/common/BackArrowSmall.svg';
import { isFirebaseError } from '@utils/firebase';
import { getSearchParameterByName } from '@utils/helpers';
import { trackEvent } from '@utils/tracking';
import validate from '@utils/validateEmail';
import DashboardErrorWrap from './dashboard/components/DashboardErrorWrap';
import Button from './shared/Button';
import Colors from './shared/Colors';
import ControlIconLink from './shared/ControlIconLink';
import Input from './shared/Input';
import Spinner from './shared/Spinner';
import TextLink from './shared/TextLink';
import useForm from './shared/useForm';

interface Props {
  style?: any;
  mobile?: boolean;
  inAccount?: boolean;
}

export default function ForgotPassword({ style, mobile, inAccount }: Props) {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const resetButtonText = t('components.reset-password');
  const [buttonText, setButtonText] = useState<any>(resetButtonText);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showNoEmail, setShowNoEmail] = useState(false);
  const [showTooMany, setShowTooMany] = useState(false);
  const { values, errors, handleChange, handleSubmit } = useForm(handleForgotPassword, (vs) => validate(vs, t));
  const backLinkText = t(inAccount ? 'components.back' : 'components.nevermind');
  const backLinkIcon = inAccount ? <BackArrowSmallIcon /> : null;
  const location = useLocation();
  const checkout = getSearchParameterByName<string>('checkout', location);
  const consultation = getSearchParameterByName<string>('consultation', location);
  const appClient = getSearchParameterByName<string>('appClient', location);

  function resetForm() {
    setShowNoEmail(false);
    setShowTooMany(false);
    setButtonDisabled(false);
    setButtonText(resetButtonText);
  }

  function handleForgotPasswordChange(e: React.FormEvent<Element>) {
    resetForm();
    handleChange(e);
  }

  function handleForgotPasswordSubmit(e: React.FormEvent<Element>) {
    resetForm();
    handleSubmit(e);
  }

  const getBooleanQueryParam = (value: string | null): string => (value === 'true' ? '1' : '0');

  const getResetPasswordUrl = (params: { checkout: string | null; consultation: string | null; appClient: string | null }) => {
    const queryParams = new URLSearchParams({
      checkout: getBooleanQueryParam(params.checkout).toString(),
      consultation: getBooleanQueryParam(params.consultation).toString(),
      appClient: getBooleanQueryParam(params.appClient).toString()
    });
    return `${location.origin}/reset-password?${queryParams.toString()}`;
  };

  async function handleForgotPassword() {
    trackEvent('forgot_password_request');
    try {
      setButtonText(<Spinner />);

      if (state.firebaseAuth) {
        await sendPasswordResetEmail(state.firebaseAuth, values.email, {
          url: getResetPasswordUrl({
            checkout,
            consultation,
            appClient
          })
        });

        setButtonText(t('components.check-email'));
        setButtonDisabled(true);
      }
    } catch (err) {
      console.error(err);
      if (isFirebaseError(err)) {
        trackEvent('forgot_password_error', { error: err.code });
        if (err.code && err.code === AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER) {
          setShowTooMany(true);
        }
      } else {
        setButtonDisabled(true);
        setShowNoEmail(true);
      }
      setButtonText(resetButtonText);
    }
  }

  return (
    <Container style={style}>
      <TitleHeading>{t('components.reset-pass')}</TitleHeading>
      <DescriptionHeading>{t('components.dont-worry-long')}</DescriptionHeading>
      <form onSubmit={handleForgotPasswordSubmit} noValidate={true}>
        {showNoEmail && (
          <DashboardErrorWrap>
            {t('components.no-account')}
            <TextLink $color={Colors.navyBlue} to={'/dashboard/login'}>
              {t('components.create-account')}
            </TextLink>
          </DashboardErrorWrap>
        )}
        {showTooMany && <DashboardErrorWrap>{t('components.too-many-requests')}</DashboardErrorWrap>}
        <Input
          placeholder={t('components.email')}
          name={'email'}
          onChange={handleForgotPasswordChange}
          value={values.email || ''}
          errorMessage={errors.email}
          mobile={mobile || inAccount}
        />
        <ButtonWrap>
          <BackControlIconLinkWrap>
            <ControlIconLink icon={backLinkIcon} text={backLinkText} onClick={() => window.history.back()} hideMobileIcon />
          </BackControlIconLinkWrap>
          <MenuButton disabled={buttonDisabled} type="submit">
            {buttonText}
          </MenuButton>
        </ButtonWrap>
      </form>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: auto;
  max-width: 680px;
  padding: 50px 60px 60px 60px;

  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

const TitleHeading = styled(H1)`
  width: 100%;
  margin-bottom: 20px;
`;

const DescriptionHeading = styled(H3)`
  font-weight: 400;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  margin-top: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-top: 0;
    margin-bottom: 0;
    flex-direction: column-reverse;
    gap: 15px;
  }
`;

const BackControlIconLinkWrap = styled.div`
  display: inline-flex;

  > div {
    padding: 10px;

    @media (max-width: 768px) {
      margin: 0 auto;
    }
  }
`;

const MenuButton = styled(Button)`
  min-width: 282px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
